import { observer } from 'mobx-react-lite'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { Dropdown, DropdownContent, DropdownItem } from 'shared/ui/Dropdown'
import { Box, Typography } from 'shared/ui'
import { LINK_AUTO_REMINDERS_LEARN_MORE } from 'shared/constants/links'
import { ChatbotFollowUpAssertiveness, type IChatbotFollowUpAssertiveness } from 'entities/Chatbot'
import { ChatbotTooltip } from 'pages/chatbot/ui/ChatbotTooltip'

import styles from './styles.module.scss'
import { type ITabsListItem, SettingsTabs } from './SettingsTabs'

export interface IFollowUpWindowProps {
  followUpWindow: number
  followUpAssertiveness: IChatbotFollowUpAssertiveness
  setFollowUpWindow: (value: number) => void
  setFollowUpAssertiveness: (value: IChatbotFollowUpAssertiveness) => void
  tabsClassName?: string
}

export const AutoFollowUp = observer(
  ({
    followUpWindow,
    followUpAssertiveness,
    setFollowUpWindow,
    setFollowUpAssertiveness,
    tabsClassName,
  }: IFollowUpWindowProps) => {
    const followUpAssertivenessList: ITabsListItem[] = [
      {
        value: ChatbotFollowUpAssertiveness.Subtle,
        label: 'Subtle',
        icon: 'chart1',
      },
      {
        value: ChatbotFollowUpAssertiveness.Steady,
        label: 'Steady',
        icon: 'chart2',
      },
      {
        value: ChatbotFollowUpAssertiveness.Assertive,
        label: 'Assertive',
        icon: 'chart3',
      },
    ]

    return (
      <>
        <div className={styles.followUpWindowWrapper}>
          <span className={styles.followUpWindowLabel}>Follow-up window</span>
          <Box display={'flex'} alignItems={'center'} paddingRight={'20px'}>
            <Dropdown
              width={59}
              placement='bottom'
              triggerComponent={() => (
                <TextFieldPlaceholder
                  className={styles.followUpValue}
                  size='small'
                  variant='outline'
                  placeholder={followUpWindow}
                  rightActions={[makeTextFieldIcon({ icon: 'chevronDown', fontSize: 12 })]}
                />
              )}
              customComponent={(close) => (
                <DropdownContent>
                  {Object.values([
                    { value: 3, label: '3' },
                    { value: 7, label: '7' },
                    { value: 14, label: '14' },
                  ]).map((item, index) => (
                    <DropdownItem
                      key={item.value}
                      item={{
                        id: index,
                        label: item.label,
                        onClick: () => {
                          close()
                          setFollowUpWindow(item.value)
                        },
                      }}
                    />
                  ))}
                </DropdownContent>
              )}
            />
            <span className={styles.daysLabel}>Days</span>
          </Box>
        </div>
        <div className={styles.followUpAssertivenessWrapper}>
          <Typography
            variant='text-input-field-r'
            color='tertiary'
            ariaLabel={'followUpAssertivenessLabel'}
            className={styles.followUpAssertivenessLabel}
            marginBottom={12}
          >
            How frequently should the AI Agent follow up?
            <ChatbotTooltip
              tooltipProps={{
                label: (
                  <>
                    The agent follows up within the set window: Subtle sends fewer messages, Steady
                    follows a steady pace, and Assertive maximizes engagement.
                    <br />
                    <a
                      href={LINK_AUTO_REMINDERS_LEARN_MORE}
                      target='_blank'
                      rel='noreferrer'
                      key={'learnMore'}
                    >
                      Learn more
                    </a>
                  </>
                ),
                placement: 'top',
                breakWord: true,
                maxWidth: 360,
              }}
              fontSize={14}
            />
          </Typography>
          <SettingsTabs
            tabsList={followUpAssertivenessList}
            selectedValue={followUpAssertiveness}
            setSelectedValue={(value) =>
              setFollowUpAssertiveness(value as IChatbotFollowUpAssertiveness)
            }
            tabsClassName={tabsClassName}
          />
        </div>
      </>
    )
  }
)
